import React, { useState, useEffect } from 'react';
import { User, Trash2, Trophy } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import type { Profile } from '../lib/types';

const AccountPage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    pseudo: '',
    email: '',
    defaultLevel: ''
  });

  const niveaux = {
    'Collège': ['6ème', '5ème', '4ème', '3ème'],
    'Lycée': ['Seconde', 'Première', 'Terminale']
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        setError(null);
        
        // First, try to get the profile
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            const { data: upsertedProfile, error: upsertError } = await supabase
              .from('profiles')
              .upsert({
                id: user.id,
                email: user.email || '',
                updated_at: new Date().toISOString(),
              }, { onConflict: 'id' })
              .select()
              .single();

            if (upsertError) {
              const { data: retryData, error: retryError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single();

              if (retryError) {
                console.error('Error loading profile:', retryError);
                throw new Error('Erreur lors du chargement du profil');
              }

              setProfile(retryData);
              setFormData({
                pseudo: retryData.first_name || '',
                email: retryData.email || user.email || '',
                defaultLevel: retryData.default_level || ''
              });
            } else {
              setProfile(upsertedProfile);
              setFormData({
                pseudo: upsertedProfile.first_name || '',
                email: upsertedProfile.email || user.email || '',
                defaultLevel: upsertedProfile.default_level || ''
              });
            }
          } else {
            console.error('Error fetching profile:', fetchError);
            throw new Error('Erreur lors du chargement du profil');
          }
        } else {
          // Profile exists
          setProfile(data);
          setFormData({
            pseudo: data.first_name || '',
            email: data.email || user.email || '',
            defaultLevel: data.default_level || ''
          });
        }
      } catch (err) {
        console.error('Error in fetchProfile:', err);
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement du profil');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: formData.pseudo,
          default_level: formData.defaultLevel || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setProfile(prev => prev ? {
        ...prev,
        first_name: formData.pseudo,
        default_level: formData.defaultLevel || null,
        updated_at: new Date().toISOString()
      } : null);
      
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Erreur lors de la mise à jour du profil');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!user) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: requestError } = await supabase
        .from('requests')
        .insert({
          email: user.email,
          topic: 'Suppression de compte',
          request: `Demande de suppression de compte pour l'utilisateur ${user.id}.
            
Pseudo: ${formData.pseudo}
Email: ${user.email}`,
          status: true
        });

      if (requestError) throw requestError;

      setShowSuccess(true);
      setShowDeleteConfirm(false);

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error submitting delete request:', err);
      setError('Une erreur est survenue lors de l\'envoi de la demande de suppression');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Mon compte</h1>

      {error && (
        <div className="mb-8 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {showSuccess && (
        <div className="mb-8 bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-600">
            Votre demande de suppression de compte a bien été transmise. Nous la traiterons dans les meilleurs délais.
          </p>
        </div>
      )}

      <div className="space-y-6">
        {/* Informations personnelles */}
        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <div className="flex items-center">
              <User className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Informations personnelles</h2>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="pseudo" className="block text-sm font-medium text-gray-700">
                    Ton pseudo
                  </label>
                  <input
                    type="text"
                    id="pseudo"
                    value={formData.pseudo}
                    onChange={(e) => setFormData({ ...formData, pseudo: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="defaultLevel" className="block text-sm font-medium text-gray-700">
                    Classe par défaut
                  </label>
                  <select
                    id="defaultLevel"
                    value={formData.defaultLevel}
                    onChange={(e) => setFormData({ ...formData, defaultLevel: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value="">Aucune classe par défaut</option>
                    <optgroup label="Collège">
                      {niveaux.Collège.map((niveau) => (
                        <option key={niveau} value={niveau}>{niveau}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Lycée">
                      {niveaux.Lycée.map((niveau) => (
                        <option key={niveau} value={niveau}>{niveau}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="mt-1 text-sm text-gray-900">{formData.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Ton pseudo</p>
                  <p className="mt-1 text-sm text-gray-900">{formData.pseudo || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Classe par défaut</p>
                  <p className="mt-1 text-sm text-gray-900">{formData.defaultLevel || 'Aucune classe par défaut'}</p>
                </div>
                <div>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Modifier
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quiz Results */}
        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <div className="flex items-center">
              <Trophy className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Résultats aux quiz</h2>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <p className="text-sm text-gray-500 mb-4">
              Consultez vos résultats aux différents quiz et suivez votre progression.
            </p>
            <Link
              to="/account/quiz-results"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary-600 hover:bg-secondary-700"
            >
              <Trophy className="h-4 w-4 mr-2" />
              Voir mes résultats
            </Link>
          </div>
        </div>

        {/* Suppression du compte */}
        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 bg-gray-50">
            <div className="flex items-center">
              <Trash2 className="h-5 w-5 text-red-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Supprimer le compte</h2>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            {showDeleteConfirm ? (
              <div>
                <p className="text-sm text-gray-500 mb-4">
                  Êtes-vous sûr de vouloir demander la suppression de votre compte ? Cette action est irréversible.
                  Veuillez noter que les fiches et quizs au statut partagés ("Public") resteront disponibles dans
                  l'application, sauf demande expresse de votre part. Si vous souhaitez qu'ils soient supprimés
                  automatiquement, vous pouvez les passer en "Privé" avant de faire votre demande.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleDeleteRequest}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      'Confirmer la demande'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Supprimer mon compte
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;